import * as React from 'react';
import { ACCOUNT_ROLES, ACCOUNT_ROLES_LABELS } from '@/constants/account-roles';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  SvgIcon,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid';
import { ArrowRight as ArrowRightIcon, Warning, X as XIcon } from '@phosphor-icons/react';
import { useMutation } from '@tanstack/react-query';
import { useConfirm } from 'material-ui-confirm';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import type { Role } from '@/types/role';
import type { User } from '@/types/v2/user';
import { UserRole } from '@/types/v2/user-role';
import axios from '@/lib/v2/axios';
import { useUser } from '@/hooks/v2/use-user';
import { UserTableTile } from '@/components/core/v2/user-table-tile';

const inviteAdministratorSchema = z.object({
  email: z.string().email('Invalid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  role: z.string().nonempty('Role is required'),
  submit: z.string().optional(),
});

type InviteAdministratorFormData = z.infer<typeof inviteAdministratorSchema>;
interface InviteAdministratorModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (user: User) => void;
}

export function InviteAdministratorModal({
  open,
  onClose,
  onSuccess,
}: InviteAdministratorModalProps): React.JSX.Element {
  const [userExists, setUserExists] = React.useState<boolean>(false);
  const [existingUser, setExistingUser] = React.useState<User | null>(null);
  const [selectedRoles, setSelectedRoles] = React.useState<string[]>([ACCOUNT_ROLES.ACCOUNT_ADMIN]);
  const { account } = useUser();
  const confirm = useConfirm();
  const theme = useTheme();

  const checkIfUserExists = useMutation({
    mutationFn: async (email: string) => {
      // Url encode the email
      const encodedEmail = encodeURIComponent(email);
      const response = await axios.get(`/api/v2/users?filter[email]=${encodedEmail}&include=accountRoles`);
      return response.data;
    },
  });

  const inviteAdministratorRequest = useMutation({
    mutationFn: async (data: InviteAdministratorFormData) => {
      const response = await axios.post(`/api/v2/accounts/${account?.id}/administrators/invite`, {
        email: data.email,
        first_name: data.firstName,
        last_name: data.lastName,
        role: data.role,
      });
      return response.data;
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
  } = useForm<InviteAdministratorFormData>({
    resolver: zodResolver(inviteAdministratorSchema),
  });

  const createAdministratorRequest = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const response = await axios.post(`/api/v2/accounts/${account?.id}/administrators`, {
        user_id: userId,
        role,
      });
      return response.data;
    },
  });

  const handleClose = React.useCallback(() => {
    onClose();
    reset({
      email: '',
      firstName: '',
      lastName: '',
      role: ACCOUNT_ROLES.ACCOUNT_ADMIN,
    });
    setUserExists(false);
    setExistingUser(null);
  }, [onClose, reset]);

  const handleSuccess = React.useCallback(
    (user: User) => {
      onSuccess(user);
      handleClose();
    },
    [onSuccess, handleClose]
  );

  const handleInvite = React.useCallback(
    async (data: InviteAdministratorFormData) => {
      try {
        console.log('Inviting administrator:', data.email);
        const { users } = await checkIfUserExists.mutateAsync(data.email);

        if (users.length > 0) {
          const user = users[0];
          console.log('User already exists:', user);
          const adminRoles = [ACCOUNT_ROLES.ACCOUNT_OWNER, ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER];
          if (user.account_roles.some((role: Role) => adminRoles.includes(role.name))) {
            setError('submit', { message: 'User is already an administrator' });
            return;
          }
          setUserExists(true);
          setExistingUser(user);
          return;
        }

        const response = await inviteAdministratorRequest.mutateAsync(data);
        if (response.error) {
          toast.error('Unable to create admin');
          return;
        }
        handleSuccess(response);
      } catch (error) {
        console.error('Failed to invite administrator:', error);
      }
    },
    [checkIfUserExists, inviteAdministratorRequest, handleSuccess, setError]
  );

  const handleAddExistingUserAsAdmin = React.useCallback(async () => {
    try {
      await confirm({
        title: (
          <Box alignItems="center" display="flex" gap={1} mt={1}>
            <SvgIcon>
              <Warning color={theme.palette.warning.main} size={24} />
            </SvgIcon>
            <Typography fontWeight="bold" variant="h6">
              Upgrade to Administrator
            </Typography>
          </Box>
        ),
        description: (
          <Box>
            <Divider sx={{ mb: 2 }} />
            <Typography sx={{ mb: 2 }} variant="body1">
              This action will provide <strong>{existingUser?.email}</strong> with administrative privileges.
            </Typography>
            <Typography fontWeight="bold" sx={{ mb: 2 }} variant="body1">
              Changes that will be made:
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <ArrowRightIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography fontWeight="bold" variant="body1">
                      {ACCOUNT_ROLES_LABELS[selectedRoles[0]]}
                    </Typography>
                  }
                  secondary={`${existingUser?.email} will be added as an administrator to the account.`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ArrowRightIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography fontWeight="bold" variant="body1">
                      Email Notification
                    </Typography>
                  }
                  secondary={`An email will be sent to ${existingUser?.email} notifying them of the change.`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ArrowRightIcon />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography fontWeight="bold" variant="body1">
                      Account Owner Notification
                    </Typography>
                  }
                  secondary="An email will be sent to the account owner(s) notifying them of the change."
                />
              </ListItem>
            </List>
          </Box>
        ),
        confirmationText: (
          <Button color="warning" variant="outlined">
            Confirm
          </Button>
        ),
        cancellationText: <Button variant="text">Cancel</Button>,
      });

      if (!existingUser?.email) {
        return;
      }
      const response = await createAdministratorRequest.mutateAsync({
        userId: existingUser.id,
        role: selectedRoles[0],
      });

      if (response.error) {
        toast.error('Unable to add existing user as admin');
        return;
      }

      handleSuccess(response);
    } catch (error) {
      console.error('Failed to add existing user as admin:', error);
    }
  }, [confirm, existingUser, handleSuccess, selectedRoles, theme]);

  return (
    <Dialog maxWidth="xs" onClose={handleClose} open={open}>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
        <Stack direction="row" sx={{ alignItems: 'center', flex: '0 0 auto', justifyContent: 'space-between' }}>
          <Typography variant="h6">Invite Administrator</Typography>
          <IconButton onClick={handleClose}>
            <XIcon />
          </IconButton>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogContent sx={{ m: 0, px: 2 }}>
        <Stack direction="column">
          <Typography sx={{ color: 'text.secondary' }} variant="body2">
            Invite an administrator to the account. Administrators can manage users, properties, and more.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogContent sx={{ m: 0, px: 3 }}>
        <Stack spacing={1}>
          {errors.submit ? <Alert severity="error">{errors.submit.message}</Alert> : null}
          <Grid container spacing={2}>
            <Grid md={6} xs={12}>
              {!userExists && (
                <TextField
                  {...register('firstName')}
                  error={Boolean(errors.firstName)}
                  fullWidth
                  helperText={errors.firstName?.message}
                  label="First Name"
                  placeholder="First Name"
                  type="text"
                />
              )}
            </Grid>
            <Grid md={6} xs={12}>
              {!userExists && (
                <TextField
                  {...register('lastName')}
                  error={Boolean(errors.lastName)}
                  fullWidth
                  helperText={errors.lastName?.message}
                  label="Last Name"
                  placeholder="Last Name"
                  type="text"
                />
              )}
            </Grid>
            <Grid xs={12}>
              {!userExists && (
                <TextField
                  {...register('email')}
                  error={Boolean(errors.email)}
                  fullWidth
                  helperText={errors.email?.message}
                  label="Email"
                  placeholder="Email"
                  type="email"
                />
              )}
            </Grid>
            {!userExists && (
              <Grid md={12} xs={12}>
                <FormControl error={Boolean(errors.role)} fullWidth>
                  <FormLabel>Role</FormLabel>
                  <Select
                    {...register('role')}
                    disabled={userExists}
                    displayEmpty
                    fullWidth
                    onChange={(e) => { setSelectedRoles([e.target.value]); }}
                    value={selectedRoles[0]}
                  >
                    <MenuItem disabled value="">
                      Select a role
                    </MenuItem>
                    {[ACCOUNT_ROLES.ACCOUNT_ADMIN, ACCOUNT_ROLES.ACCOUNT_MANAGER].map((role) => (
                      <MenuItem key={role} value={role}>
                        {ACCOUNT_ROLES_LABELS[role]}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.role?.message}</FormHelperText>
                </FormControl>
              </Grid>
            )}
            {userExists ? <Stack gap={2}>
                <Alert severity="info">
                  A user with this email already exists. You can add this user as an administrator by confirming below.
                </Alert>
                {existingUser ? <UserTableTile showEmail user={existingUser} /> : null}
                <Typography fontWeight="bold" sx={{ mb: 2 }} variant="body1">
                  Current roles:
                </Typography>
                {existingUser?.account_roles && existingUser.account_roles.length > 0 ? (
                  <List>
                    {existingUser?.account_roles ? existingUser.account_roles.map((role: Role) => (
                        <ListItem key={role.id}>
                          <ListItemText primary={ACCOUNT_ROLES_LABELS[role.name]} />
                        </ListItem>
                      )) : null}
                  </List>
                ) : (
                  <Typography sx={{ mb: 2 }} variant="body1">
                    This user has no roles.
                  </Typography>
                )}
              </Stack> : null}
          </Grid>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={handleClose}>Cancel</Button>
        {userExists ? (
          <LoadingButton
            loading={createAdministratorRequest.isPending}
            onClick={handleAddExistingUserAsAdmin}
            variant="contained"
          >
            Add as Admin
          </LoadingButton>
        ) : (
          <LoadingButton
            loading={checkIfUserExists.isPending || inviteAdministratorRequest.isPending}
            onClick={handleSubmit(handleInvite)}
            variant="contained"
          >
            Invite
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
